/*
 * EmotionReactionManager
 * ----------------------
 * - キュー制御で感情アニメーションを直列化
 * - 気分ヒストリーを維持して反応の強さをチューニング
 * - エイリアス＆レジストリで表情プリセットとTokchanController.performActionを橋渡し
 */

// ================================================================================
// Type Definitions
// ================================================================================

import { logger } from '@/utils/logger.js';

/**
 * Emotion data structure for tracking emotional events
 */
export interface EmotionData {
  emotion: string;
  intensity?: number;
  confidence?: number;
  timestamp?: number;
  source?: string;
  metadata?: EmotionMetadata;
  priority?: number;
  duration?: number;
}

/**
 * Metadata associated with an emotion event
 */
export interface EmotionMetadata {
  sentimentScore?: number;
  userRole?: 'vip' | 'moderator' | 'user';
  giftValue?: number;
  lowConfidence?: boolean;
  [key: string]: unknown;
}

/**
 * Normalized emotion event with all required fields
 */
export interface NormalizedEmotionEvent {
  emotion: string;
  originalEmotion: string;
  action: string;
  intensity: number;
  priority: number;
  cooldown: number;
  timestamp: number;
  metadata: EmotionMetadata;
  source: string;
}

/**
 * Emotion registry configuration
 */
export interface EmotionConfig {
  action: string;
  baseIntensity: number;
  cooldown: number;
  priority: number;
  moodEffects: MoodEffects;
}

/**
 * Mood effects for each emotion
 */
export interface MoodEffects {
  happiness?: number;
  energy?: number;
  calm?: number;
  gratitude?: number;
  curiosity?: number;
  love?: number;
  anxiety?: number;
  anger?: number;
  surprise?: number;
  sadness?: number;
  sleepiness?: number;
  [key: string]: number | undefined;
}

/**
 * Mood state tracking all emotional dimensions
 */
export interface MoodState {
  happiness: number;
  energy: number;
  calm: number;
  gratitude: number;
  curiosity: number;
  love: number;
  anxiety: number;
  anger: number;
  surprise: number;
  sadness: number;
  sleepiness: number;
  [key: string]: number;
}

/**
 * History entry for emotion reactions
 */
export interface ReactionHistoryEntry {
  timestamp: number;
  emotion: string;
  intensity: number;
  source: string;
}

/**
 * Snapshot of current manager state
 */
export interface MoodSnapshot {
  mood: MoodState;
  queueLength: number;
  lastEvents: ReactionHistoryEntry[];
}

/**
 * Manager options for configuration
 */
export interface ManagerOptions {
  cooldown?: number;
  historySize?: number;
  moodDecay?: number;
  queueLag?: number;
  registry?: Record<string, EmotionConfig>;
  aliases?: Record<string, string>;
  initialMood?: Partial<MoodState>;
}

/**
 * Controller interface that EmotionReactionManager depends on
 */
export interface TokchanController {
  performAction(action: string, intensity: number): void;
  [key: string]: unknown;
}

/**
 * Emotion registry type
 */
export type EmotionRegistry = Record<string, EmotionConfig>;

/**
 * Emotion aliases type
 */
export type EmotionAliases = Record<string, string>;

// ================================================================================
// Constants
// ================================================================================

const DEFAULT_REGISTRY: EmotionRegistry = {
  happy: { action: 'happy', baseIntensity: 0.9, cooldown: 1400, priority: 6, moodEffects: { happiness: 0.12, energy: 0.05 } },
  excited: { action: 'excited', baseIntensity: 1.1, cooldown: 1600, priority: 8, moodEffects: { energy: 0.18, happiness: 0.1 } },
  calm: { action: 'calm', baseIntensity: 0.7, cooldown: 1800, priority: 4, moodEffects: { calm: 0.18, energy: -0.05 } },
  sad: { action: 'sad', baseIntensity: 0.85, cooldown: 1700, priority: 5, moodEffects: { happiness: -0.12, sadness: 0.15 } },
  surprised: { action: 'surprised', baseIntensity: 1.0, cooldown: 1200, priority: 6, moodEffects: { surprise: 0.18 } },
  curious: { action: 'curious', baseIntensity: 0.95, cooldown: 1100, priority: 5, moodEffects: { curiosity: 0.2 } },
  grateful: { action: 'grateful', baseIntensity: 1.0, cooldown: 1500, priority: 7, moodEffects: { gratitude: 0.22, happiness: 0.08 } },
  loving: { action: 'loving', baseIntensity: 1.05, cooldown: 1900, priority: 7, moodEffects: { love: 0.22, calm: 0.05 } },
  anxious: { action: 'anxious', baseIntensity: 0.95, cooldown: 1500, priority: 6, moodEffects: { anxiety: 0.2, calm: -0.08 } },
  angry: { action: 'angry', baseIntensity: 1.0, cooldown: 1300, priority: 7, moodEffects: { anger: 0.22, happiness: -0.08 } },
  sleepy: { action: 'sleepy', baseIntensity: 0.8, cooldown: 1800, priority: 4, moodEffects: { sleepiness: 0.22, energy: -0.1 } },
  default: { action: 'idle', baseIntensity: 0.6, cooldown: 1000, priority: 3, moodEffects: { calm: 0.05 } }
};

const DEFAULT_ALIASES: EmotionAliases = {
  smile: 'happy',
  happy_smile: 'happy',
  big_smile: 'happy',
  ecstatic: 'excited',
  gentle_wave: 'grateful',
  celebration_dance: 'excited',
  victory_dance: 'excited',
  gentle_bow: 'grateful',
  gentle_smile: 'grateful',
  sparkle: 'excited',
  fireworks: 'excited',
  subtle_glow: 'calm',
  spectacular: 'excited',
  sleepy_eye: 'sleepy',
  nervous: 'anxious',
  confusion: 'confused',
  confused: 'confused',
  surprise: 'surprised'
};

// ================================================================================
// Main Class
// ================================================================================

/**
 * EmotionReactionManager
 * Manages emotion-based reactions with queueing, mood tracking, and intensity modulation
 */
export class EmotionReactionManager {
  private controller: TokchanController;
  private options: Required<Omit<ManagerOptions, 'registry' | 'aliases' | 'initialMood'>>;
  private registry: EmotionRegistry;
  private aliases: EmotionAliases;
  private queue: NormalizedEmotionEvent[];
  private processing: boolean;
  private history: ReactionHistoryEntry[];
  private mood: MoodState;

  constructor(controller: TokchanController, options: ManagerOptions = {}) {
    this.controller = controller;
    this.options = {
      cooldown: options.cooldown ?? 900,
      historySize: options.historySize ?? 60,
      moodDecay: options.moodDecay ?? 0.08,
      queueLag: options.queueLag ?? 120
    };

    this.registry = { ...DEFAULT_REGISTRY, ...(options.registry || {}) };
    this.aliases = { ...DEFAULT_ALIASES, ...(options.aliases || {}) };

    this.queue = [];
    this.processing = false;
    this.history = [];
    this.mood = {
      happiness: 0.5,
      energy: 0.5,
      calm: 0.5,
      gratitude: 0.3,
      curiosity: 0.3,
      love: 0.3,
      anxiety: 0.2,
      anger: 0.2,
      surprise: 0.2,
      sadness: 0.2,
      sleepiness: 0.2,
      ...(options.initialMood || {})
    };
  }

  /**
   * Enqueue an emotion event for processing
   */
  public enqueue(rawEvent: EmotionData): void {
    if (!rawEvent || !rawEvent.emotion) {
      logger.warn('EmotionReactionManager.enqueue: invalid event', rawEvent);
      return;
    }

    const event = this._normaliseEvent(rawEvent);
    if (!event) {
      return;
    }

    this.queue.push(event);
    this.queue.sort((a, b) => b.priority - a.priority || a.timestamp - b.timestamp);
    this._processQueue();
  }

  /**
   * Register a new emotion or update existing emotion configuration
   */
  public registerEmotion(emotionKey: string, config: Partial<EmotionConfig>): void {
    if (!emotionKey || !config) return;
    this.registry[emotionKey] = { ...(this.registry[emotionKey] || {} as EmotionConfig), ...config } as EmotionConfig;
  }

  /**
   * Set an alias for an emotion
   */
  public setAlias(aliasKey: string, targetEmotion: string): void {
    if (!aliasKey || !targetEmotion) return;
    this.aliases[aliasKey] = targetEmotion;
  }

  /**
   * Get a snapshot of current mood state and queue
   */
  public getMoodSnapshot(): MoodSnapshot {
    return JSON.parse(JSON.stringify({
      mood: this.mood,
      queueLength: this.queue.length,
      lastEvents: this.history.slice(-5)
    }));
  }

  /**
   * Normalize and validate an emotion event
   */
  private _normaliseEvent(event: EmotionData): NormalizedEmotionEvent | null {
    const now = Date.now();
    const originalKey = String(event.emotion || '').toLowerCase();
    const emotionKey = this._resolveEmotionKey(originalKey);
    const mapping = this.registry[emotionKey] || this.registry.default;

    if (!mapping) {
      logger.warn('EmotionReactionManager: no mapping for emotion', emotionKey);
      return null;
    }

    const baseIntensity = event.intensity != null ? event.intensity : mapping.baseIntensity;
    const adjustedIntensity = this._applyModifiers(baseIntensity, event, mapping);
    const priority = event.priority != null ? event.priority : mapping.priority;
    const cooldown = event.duration || mapping.cooldown || this.options.cooldown;

    return {
      emotion: emotionKey,
      originalEmotion: originalKey,
      action: mapping.action,
      intensity: this._clamp(adjustedIntensity, 0.2, 2.0),
      priority: priority,
      cooldown,
      timestamp: now,
      metadata: event.metadata || {},
      source: event.source || 'unknown'
    };
  }

  /**
   * Resolve emotion key through aliases
   */
  private _resolveEmotionKey(key: string): string {
    if (this.registry[key]) {
      return key;
    }
    if (this.aliases[key]) {
      const alias = this.aliases[key];
      if (this.registry[alias]) {
        return alias;
      }
    }
    return this.registry[key] ? key : 'default';
  }

  /**
   * Apply intensity modifiers based on event metadata and mood state
   */
  private _applyModifiers(baseIntensity: number, event: EmotionData, mapping: EmotionConfig): number {
    let intensity = baseIntensity;
    const metadata = event.metadata || {};

    // Sentiment score modifier
    if (metadata.sentimentScore != null) {
      intensity *= 0.8 + Math.min(Math.abs(metadata.sentimentScore), 1) * 0.4;
    }

    // User role modifier
    if (metadata.userRole === 'vip' || metadata.userRole === 'moderator') {
      intensity *= 1.15;
    }

    // Gift value modifier
    if (metadata.giftValue) {
      intensity *= 1 + Math.min(metadata.giftValue / 2000, 0.35);
    }

    // Source and confidence modifiers
    if (event.source === 'system' || metadata.lowConfidence) {
      intensity *= 0.85;
    }

    // Mood-based modifiers - suppress high-energy emotions when already energetic
    if (['excited', 'angry'].includes(mapping.action) && this.mood.energy > 0.75) {
      intensity *= 0.9;
    }

    // Suppress calm emotions when already calm
    if (mapping.action === 'calm' && this.mood.calm > 0.7) {
      intensity *= 0.85;
    }

    return intensity;
  }

  /**
   * Process the emotion queue sequentially
   */
  private _processQueue(): void {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    const nextEvent = this.queue.shift();
    if (!nextEvent) {
      return;
    }

    this.processing = true;

    this._updateMood(nextEvent);
    this._executeEvent(nextEvent)
      .catch((error: Error) => {
        logger.error('EmotionReactionManager: failed to execute event', error);
      })
      .finally(() => {
        setTimeout(() => {
          this.processing = false;
          this._processQueue();
        }, Math.max(nextEvent.cooldown, this.options.queueLag));
      });
  }

  /**
   * Execute an emotion event by calling controller's performAction
   */
  private async _executeEvent(event: NormalizedEmotionEvent): Promise<void> {
    if (!this.controller || typeof this.controller.performAction !== 'function') {
      logger.warn('EmotionReactionManager: TokchanController not ready');
      return;
    }

    try {
      this.controller.performAction(event.action, event.intensity);
      this.history.push({
        timestamp: event.timestamp,
        emotion: event.emotion,
        intensity: event.intensity,
        source: event.source
      });
      if (this.history.length > this.options.historySize) {
        this.history.splice(0, this.history.length - this.options.historySize);
      }
    } catch (error) {
      logger.error('EmotionReactionManager: performAction failed', error);
    }
  }

  /**
   * Update mood state based on emotion event
   */
  private _updateMood(event: NormalizedEmotionEvent): void {
    const mapping = this.registry[event.emotion] || this.registry.default;
    if (!mapping) {
      logger.warn('EmotionReactionManager: no mapping found for mood update');
      return;
    }

    const effects = mapping.moodEffects || {};
    const decay = 1 - this.options.moodDecay;

    // Apply decay to all mood dimensions
    Object.keys(this.mood).forEach((key) => {
      const currentValue = this.mood[key];
      if (currentValue !== undefined) {
        this.mood[key] = this._clamp(currentValue * decay, 0, 1);
      }
    });

    // Apply emotion-specific mood effects
    Object.entries(effects).forEach(([key, value]) => {
      const base = this.mood[key] != null ? this.mood[key] : 0.5;
      const delta = (value ?? 0) * event.intensity;
      this.mood[key] = this._clamp(base + delta, 0, 1);
    });
  }

  /**
   * Clamp a value between min and max
   */
  private _clamp(value: number, min: number = 0, max: number = 1): number {
    return Math.max(min, Math.min(max, value));
  }
}

// ================================================================================
// Global Export (Browser Compatibility)
// ================================================================================

if (typeof window !== 'undefined') {
  window.EmotionReactionManager = EmotionReactionManager;
}

// CommonJS compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EmotionReactionManager;
}

// Default export for ES modules
export default EmotionReactionManager;
